import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { VariableSizeList as VList } from "react-window";
import api from "@/lib/api";
import CatalogItemDetail from "@/components/catalog/CatalogItemDetail";
import { LaborHoursInput } from "@/lib/laborUnit";
import { Combobox } from "@/components/ui/form-kit";
import { CostCodeCombobox } from "@/components/CostCodeSelect";
import { QtyInput } from "@/lib/qtyInput";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2, Search, Package, Layers, Truck, ChevronDown, ChevronRight, X, TrendingUp, TrendingDown, Download, Upload, CheckCircle2, AlertTriangle, Loader2, FileUp, RotateCcw, Pencil, Star, Users, GripVertical, Tags } from "lucide-react";
import { KindSelect } from "@/components/KindSelect";
import { AutoTagKindsDialog } from "@/components/catalog/AutoTagKinds";

const TYPES = [
  { key: "all", label: "All" },
  { key: "line_item", label: "Line items" },
  { key: "assembly", label: "Assemblies" },
  { key: "equipment", label: "Equipment" },
];
const TYPE_META = {
  line_item: { label: "Line item", cls: "bg-blue-50 text-blue-700", Icon: Package },
  assembly: { label: "Assembly", cls: "bg-violet-50 text-violet-700", Icon: Layers },
  equipment: { label: "Equipment", cls: "bg-amber-50 text-amber-700", Icon: Truck },
};
const SRC_LABEL = { takeoff: "Takeoff", estimate: "Estimate", catalog: "Catalog", import: "Import", seed: "Starter" };

const relTime = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return "just now";
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 129600) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

function SourceChip({ source }) {
  if (!source) return <span className="text-slate-300">—</span>;
  const proj = source.project_name;
  return (
    <span className="text-[11px] text-slate-500">
      {SRC_LABEL[source.kind] || "Catalog"}{proj ? <> · <span className="text-slate-600">{proj}</span></> : ""}
    </span>
  );
}

const money4 = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 4 })}`);

// Rough "time remaining" from how fast rows have committed so far (items/sec since start).
const etaText = (p) => {
  if (!p || !p.total || !p.done || !p.startedAt) return null;
  const elapsed = (Date.now() - p.startedAt) / 1000;
  if (elapsed < 2 || p.done < 1) return null;
  const rate = p.done / elapsed;
  if (rate <= 0) return null;
  const secs = Math.round(Math.max(0, p.total - p.done) / rate);
  if (secs <= 1) return "almost done";
  const m = Math.floor(secs / 60), s = secs % 60;
  return m > 0 ? `~${m}m ${s}s left` : `~${s}s left`;
};

function IndexChip({ item }) {
  if (!item.commodity_index_id || !item.index_name) return null;
  const chg = Number(item.index_change_pct) || 0;
  const up = chg >= 0;
  const Tone = up ? TrendingUp : TrendingDown;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700"
      title={`Auto-adjusts with ${item.index_name}. Baseline ${money4(item.unit_material_cost)} → now ${money4(item.material_adjusted)}`}
      data-testid={`catalog-index-chip-${item.catalog_id}`}>
      <Tone className="w-3 h-3" /> {item.index_name}
      <span className="font-mono text-emerald-800">{money4(item.material_adjusted)}</span>
    </span>
  );
}

function UsageChip({ usage }) {
  const n = usage?.count || 0;
  if (!n) return <span className="text-[11px] text-slate-300">unused</span>;
  return (
    <span className="text-[11px] text-slate-500" title={usage?.last_used_at ? new Date(usage.last_used_at).toLocaleString() : ""}>
      used <b className="text-slate-700 font-mono">{n}×</b>{usage?.last_project_name ? <> · last on <span className="text-slate-600">{usage.last_project_name}</span></> : ""}{usage?.last_used_at ? <span className="text-slate-400"> ({relTime(usage.last_used_at)})</span> : ""}
    </span>
  );
}

function CatalogVRow({ index, style, data }) {
  const { rows, sel, canEdit, toggleSel, setEditor, del, toggleCat, collapsed, openDetail, toggleFav, toggleTeamFav } = data;
  const row = rows[index];
  if (!row) return null;
  if (row.kind === "header") {
    const open = !collapsed.has(row.cat);
    return (
      <button style={style} onClick={() => toggleCat(row.cat)}
        className="flex items-center gap-2 px-3 bg-slate-50 border-y border-slate-100 text-left w-full" data-testid={`catalog-cat-${row.cat}`}>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
        <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide flex-1 truncate">{row.cat}</span>
        <span className="text-[10px] text-slate-400">{row.count}</span>
      </button>
    );
  }
  const it = row.it;
  const M = TYPE_META[it.type] || TYPE_META.line_item;
  return (
    <div style={style} className="flex items-center gap-3 px-3 border-b border-slate-50 hover:bg-slate-50/60" data-testid={`catalog-row-${it.catalog_id}`}>
      {canEdit && (it.type === "line_item"
        ? <input type="checkbox" checked={sel.has(it.catalog_id)} onChange={() => toggleSel(it.catalog_id)} className="shrink-0 accent-[#003E87]" data-testid={`catalog-select-${it.catalog_id}`} />
        : <span className="w-4 shrink-0" />)}
      <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded shrink-0 ${M.cls}`}><M.Icon className="w-3 h-3" /> {M.label}</span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-800 truncate">
          <button onClick={() => openDetail(it.catalog_id)} className="hover:underline text-left" data-testid={`catalog-open-${it.catalog_id}`}>{it.name}</button>
          {it.cost_code ? <span className="ml-1.5 text-[10px] font-mono text-[#003E87]">[{it.cost_code}]</span> : null}
        </div>
        <div className="flex items-center gap-2 flex-nowrap overflow-hidden whitespace-nowrap"><SourceChip source={it.source} /><span className="text-slate-200">·</span><UsageChip usage={it.usage} /><IndexChip item={it} /></div>
      </div>
      <button onClick={() => toggleFav(it)} title={it.favorited ? "Remove star" : "Star for quick access"} data-testid={`catalog-fav-${it.catalog_id}`} className={`shrink-0 ${it.favorited ? "text-amber-500" : "text-slate-300 hover:text-amber-500"}`}><Star className={`w-4 h-4 ${it.favorited ? "fill-amber-400" : ""}`} /></button>
      {(canEdit || it.team_favorited) && (
        <button onClick={canEdit ? () => toggleTeamFav(it) : undefined} disabled={!canEdit} title={it.team_favorited ? "Team favorite — shared with everyone" : "Add to team favorites (shared with all estimators)"} data-testid={`catalog-teamfav-${it.catalog_id}`} className={`shrink-0 ${it.team_favorited ? "text-indigo-600" : "text-slate-300 hover:text-indigo-600"} ${!canEdit ? "cursor-default" : ""}`}><Users className="w-4 h-4" /></button>
      )}
      {canEdit && <button onClick={() => setEditor({ ...it })} className="text-slate-300 hover:text-[#003E87] shrink-0" data-testid={`catalog-edit-${it.catalog_id}`}><Pencil className="w-3.5 h-3.5" /></button>}
      {canEdit && <button onClick={() => del(it)} className="text-slate-300 hover:text-rose-600 shrink-0" data-testid={`catalog-del-${it.catalog_id}`}><Trash2 className="w-4 h-4" /></button>}
    </div>
  );
}

export default function UnifiedCatalog({ canEdit }) {
  const PAGE = 150;
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ all: 0, line_item: 0, assembly: 0, equipment: 0 });
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [suggestion, setSuggestion] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [codes, setCodes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [indices, setIndices] = useState([]);
  const [sel, setSel] = useState(() => new Set());
  const [bulkIdx, setBulkIdx] = useState("");
  const [confirmItem, setConfirmItem] = useState(null);
  const [editor, setEditor] = useState(null);
  const [ioOpen, setIoOpen] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [teamFavs, setTeamFavs] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [listH, setListH] = useState(560);
  const listRef = useRef(null);
  const loadingRef = useRef(false);

  // Debounce the search box — hit the server at most a few times a second.
  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  const fetchPage = useCallback(async (from) => {
    if (loadingRef.current) return;
    loadingRef.current = true; setLoading(true);
    try {
      const r = await api.get("/catalog", { params: { type: filter, q: dq || undefined, skip: from, limit: PAGE } });
      const d = r.data || {};
      setItems((prev) => (from === 0 ? (d.items || []) : [...prev, ...(d.items || [])]));
      setCounts(d.counts || { all: 0, line_item: 0, assembly: 0, equipment: 0 });
      setTotal(d.total || 0);
      setSuggestion(dq ? (d.suggestion || null) : null);
    } catch { /* ignore */ }
    finally { loadingRef.current = false; setLoading(false); }
  }, [filter, dq]);

  // Reload from the top whenever the tab or (debounced) search changes.
  useEffect(() => { setItems([]); listRef.current?.scrollTo(0); fetchPage(0); }, [fetchPage]);
  const reloadTop = () => fetchPage(0);

  useEffect(() => { api.get("/cost-codes").then((r) => setCodes([...(r.data.standard || []), ...(r.data.custom || [])])).catch(() => {}); }, []);
  useEffect(() => { api.get("/catalog/settings").then((r) => setCategories(r.data.equipment_categories || [])).catch(() => {}); }, []);
  // Smart category suggestions: everything already in use across the catalog + configured equipment categories.
  const allCats = useMemo(() => [...new Set([...(categories || []), ...items.map((i) => i.category).filter(Boolean)])].sort((a, b) => String(a).localeCompare(String(b))), [categories, items]);
  useEffect(() => { api.get("/commodity-indices").then((r) => setIndices(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    const calc = () => setListH(Math.max(340, window.innerHeight - 330));
    calc(); window.addEventListener("resize", calc); return () => window.removeEventListener("resize", calc);
  }, []);

  const del = (it) => setConfirmItem(it);
  const loadTeamFavs = useCallback(() => {
    api.get("/catalog/pinned").then((r) => setTeamFavs((r.data?.team) || [])).catch(() => {});
  }, []);
  useEffect(() => { loadTeamFavs(); }, [loadTeamFavs]);
  const dropTeam = (to) => {
    if (dragIdx == null || dragIdx === to) { setDragIdx(null); return; }
    setTeamFavs((arr) => {
      const next = [...arr]; const [m] = next.splice(dragIdx, 1); next.splice(to, 0, m);
      api.post("/catalog/team-favorites/reorder", { order: next.map((x) => x.catalog_id) })
        .then(() => toast.success("Team favorite order saved")).catch(() => toast.error("Could not save order"));
      return next;
    });
    setDragIdx(null);
  };
  const toggleFav = useCallback(async (it) => {
    try {
      const r = await api.post(`/catalog/${it.catalog_id}/favorite`);
      const fav = !!r.data?.favorited;
      setItems((arr) => arr.map((x) => (x.catalog_id === it.catalog_id ? { ...x, favorited: fav } : x)));
    } catch { toast.error("Could not update favorite"); }
  }, []);
  const toggleTeamFav = useCallback(async (it) => {
    try {
      const r = await api.post(`/catalog/${it.catalog_id}/team-favorite`);
      const tf = !!r.data?.team_favorited;
      setItems((arr) => arr.map((x) => (x.catalog_id === it.catalog_id ? { ...x, team_favorited: tf } : x)));
      loadTeamFavs();
      toast.success(tf ? "Added to team favorites — everyone sees it" : "Removed from team favorites");
    } catch (e) { toast.error(e.response?.data?.detail || "Only library managers can set team favorites"); }
  }, []);
  const doDelete = async () => {
    if (!confirmItem) return;
    try { await api.delete(`/catalog/${confirmItem.catalog_id}`); toast.success("Removed"); setConfirmItem(null); reloadTop(); }
    catch { toast.error("Could not remove"); setConfirmItem(null); }
  };
  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleCat = (cat) => setCollapsed((c) => { const n = new Set(c); n.has(cat) ? n.delete(cat) : n.add(cat); return n; });
  const applyBulk = async () => {
    try {
      const r = await api.post("/catalog/bulk-index", { catalog_ids: [...sel], commodity_index_id: bulkIdx || null });
      toast.success(`${bulkIdx ? "Linked" : "Unlinked"} ${r.data.updated} item${r.data.updated === 1 ? "" : "s"}`);
      setSel(new Set()); setBulkIdx(""); reloadTop();
    } catch { toast.error("Could not update"); }
  };

  // Group the loaded page(s) by category (server already sorts by category, name),
  // then flatten into header + item rows for the virtualized list.
  const grouped = useMemo(() => {
    const g = new Map();
    items.forEach((x) => { const k = x.category || "Uncategorized"; if (!g.has(k)) g.set(k, []); g.get(k).push(x); });
    return [...g.entries()];
  }, [items]);
  const rows = useMemo(() => {
    const out = [];
    for (const [cat, list] of grouped) {
      out.push({ kind: "header", cat, count: list.length });
      if (!collapsed.has(cat)) for (const it of list) out.push({ kind: "item", it });
    }
    return out;
  }, [grouped, collapsed]);
  useEffect(() => { listRef.current?.resetAfterIndex(0); }, [rows]);

  const hasMore = items.length < total;
  const onItemsRendered = ({ visibleStopIndex }) => {
    if (!loadingRef.current && hasMore && visibleStopIndex >= rows.length - 12) fetchPage(items.length);
  };
  const sizeAt = (i) => (rows[i]?.kind === "header" ? 40 : 68);
  const itemData = useMemo(() => ({ rows, sel, canEdit, toggleSel, setEditor, del, toggleCat, collapsed, openDetail: setDetailId, toggleFav, toggleTeamFav }), [rows, sel, canEdit, collapsed, toggleFav, toggleTeamFav]);

  const newItem = (type) => setEditor({
    type,
    name: "", category: "", cost_code: "", measure_type: "count", uom: "ea",
    ...(type === "line_item" ? { unit_labor_hours: "", unit_material_cost: "" } : {}),
    ...(type === "assembly" ? { per_unit: 1, time_basis: "man", components: [{ name: "", qty: 1, man_hours: 0, material_cost: 0, multiplier: 1, waste_pct: 0 }] } : {}),
    ...(type === "equipment" ? { rate: "", rate_basis: "day", fuel_mode: "gph", fuel_gph: "", delivery_cost: "" } : {}),
  });

  return (
    <div className="space-y-3" data-testid="unified-catalog">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, category, cost code…" className="h-9 pl-8" data-testid="catalog-search" />
        </div>
        {canEdit && (
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" onClick={() => newItem("line_item")} data-testid="catalog-add-line-item"><Plus className="w-3.5 h-3.5 mr-1" /> Line item</Button>
            <Button size="sm" variant="outline" onClick={() => newItem("assembly")} data-testid="catalog-add-assembly"><Plus className="w-3.5 h-3.5 mr-1" /> Assembly</Button>
            <Button size="sm" variant="outline" onClick={() => newItem("equipment")} data-testid="catalog-add-equipment"><Plus className="w-3.5 h-3.5 mr-1" /> Equipment</Button>
            <Button size="sm" variant="outline" onClick={() => setTagOpen(true)} data-testid="catalog-auto-tag" title="Give every untagged pile, row, module, inverter… item a kind from its name"><Tags className="w-3.5 h-3.5 mr-1" /> Auto-tag kinds</Button>
            <Button size="sm" onClick={() => setIoOpen(true)} className="bg-[#003E87] hover:bg-[#00306a]" data-testid="catalog-import-export"><FileUp className="w-3.5 h-3.5 mr-1" /> Import / Export</Button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5">
        {TYPES.map((t) => (
          <button key={t.key} onClick={() => setFilter(t.key)} data-testid={`catalog-filter-${t.key}`}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${filter === t.key ? "bg-[#003E87] text-white border-[#003E87]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
            {t.label} <span className={filter === t.key ? "text-white/70" : "text-slate-400"}>{counts[t.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {suggestion && dq && (
        <button onClick={() => setQ(suggestion)} data-testid="catalog-didyoumean" className="text-xs text-slate-500 hover:text-[#003E87]">
          Did you mean <span className="font-semibold text-[#003E87]">{suggestion}</span>?
        </button>
      )}

      {canEdit && teamFavs.length > 0 && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-2.5" data-testid="team-fav-manager">
          <div className="text-[11px] font-semibold text-indigo-700 flex items-center gap-1.5 mb-1.5"><Users className="w-3.5 h-3.5" /> Team favorites <span className="font-normal text-indigo-400">· drag to set the order the crew sees them</span></div>
          <div className="flex flex-col gap-1">
            {teamFavs.map((t, i) => (
              <div key={t.catalog_id} draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => dropTeam(i)}
                onDragEnd={() => setDragIdx(null)}
                data-testid={`team-fav-row-${t.catalog_id}`}
                className={`flex items-center gap-2 bg-white border rounded px-2 py-1 text-xs cursor-grab active:cursor-grabbing transition-colors ${dragIdx === i ? "border-indigo-400 opacity-60" : "border-slate-200"}`}>
                <GripVertical className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                <span className="font-mono text-[10px] text-slate-400 w-4 shrink-0">{i + 1}</span>
                <span className="flex-1 min-w-0 truncate text-slate-700">{t.name}{t.cost_code ? <span className="ml-1 text-[10px] font-mono text-[#003E87]">[{t.cost_code}]</span> : null}</span>
                <span className="text-[10px] text-slate-400 shrink-0 capitalize">{t.type === "assembly" ? "assembly" : (t.measure_type || "count")}</span>
                <button onClick={() => toggleTeamFav(t)} title="Remove from team favorites" data-testid={`team-fav-remove-${t.catalog_id}`} className="text-slate-300 hover:text-rose-600 shrink-0"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-slate-500">One home for every reusable item — line items, assemblies and equipment. Anything you build in Takeoff or Estimating auto-saves here with its source and a running usage history.</p>

      {canEdit && sel.size > 0 && (
        <div className="flex items-center gap-2 p-2 rounded-lg border border-[#003E87]/30 bg-[#003E87]/5" data-testid="catalog-bulk-bar">
          <span className="text-xs font-medium text-[#003E87]">{sel.size} line item{sel.size === 1 ? "" : "s"} selected</span>
          <select value={bulkIdx} onChange={(e) => setBulkIdx(e.target.value)} className="h-8 text-xs border border-slate-200 rounded px-2 bg-white" data-testid="catalog-bulk-index">
            <option value="">— unlink (no index) —</option>
            {indices.map((i) => <option key={i.index_id} value={i.index_id}>{i.name}</option>)}
          </select>
          <Button size="sm" onClick={applyBulk} className="bg-[#003E87] hover:bg-[#00306a] h-8" data-testid="catalog-bulk-apply">{bulkIdx ? "Link to index" : "Unlink"}</Button>
          <button onClick={() => setSel(new Set())} className="text-xs text-slate-500 hover:text-slate-700 ml-auto" data-testid="catalog-bulk-clear">Clear</button>
        </div>
      )}

      <div className="text-[11px] text-slate-400 h-4" data-testid="catalog-showing">
        {total ? `Showing ${items.length.toLocaleString()} of ${total.toLocaleString()}` : ""}{loading && items.length > 0 ? " · loading…" : ""}
      </div>

      {items.length === 0 && loading ? (
        <Card className="p-8 text-center text-sm text-slate-400" data-testid="catalog-loading"><Loader2 className="w-4 h-4 animate-spin inline mr-1.5" /> Loading catalog…</Card>
      ) : rows.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-400" data-testid="catalog-empty">Nothing matches{dq ? ` "${dq}"` : ""}. {canEdit ? "Add an item above, or build one in the Takeoff tool — it saves here automatically." : ""}</Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <VList ref={listRef} height={listH} width="100%" itemCount={rows.length} itemSize={sizeAt}
            overscanCount={10} itemData={itemData} onItemsRendered={onItemsRendered}>
            {CatalogVRow}
          </VList>
        </Card>
      )}

      {editor && <ItemEditor item={editor} codes={codes} categories={categories} categoryOptions={allCats} indices={indices} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); reloadTop(); }} />}
      {detailId && <CatalogItemDetail catalogId={detailId} canEdit={canEdit} onClose={() => setDetailId(null)} onEdit={(it) => { setDetailId(null); setEditor({ ...it }); }} />}
      <CatalogImportExport open={ioOpen} onOpenChange={setIoOpen} onImported={reloadTop} />
      <AutoTagKindsDialog open={tagOpen} onOpenChange={setTagOpen} onApplied={reloadTop} />
      <AlertDialog open={!!confirmItem} onOpenChange={(o) => !o && setConfirmItem(null)}>
        <AlertDialogContent data-testid="catalog-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from catalog?</AlertDialogTitle>
            <AlertDialogDescription>"{confirmItem?.name}" will be removed from the catalog. Bids that already used it keep their frozen copy.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="catalog-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="catalog-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ItemEditor({ item, codes, categories, categoryOptions = [], indices, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ ...item }));
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const type = f.type;
  const meta = TYPE_META[type] || TYPE_META.line_item;
  const num = (v) => (v === "" || v == null ? "" : Number(v));

  const setC = (i, k, v) => setF((e) => { const c = [...(e.components || [])]; c[i] = { ...c[i], [k]: v }; return { ...e, components: c }; });
  const addC = () => setF((e) => ({ ...e, components: [...(e.components || []), { name: "", qty: 1, man_hours: 0, material_cost: 0, multiplier: 1, waste_pct: 0 }] }));
  const delC = (i) => setF((e) => ({ ...e, components: e.components.filter((_, j) => j !== i) }));

  const save = async () => {
    if (!f.name?.trim()) { toast.error("Name is required."); return; }
    if (type === "line_item" && !f.commodity_index_id && !(f.no_index_reason || "").trim()) {
      toast.error("Link a commodity index, or type a reason for not linking one.");
      return;
    }
    try {
      if (type === "assembly") await api.post("/assemblies", f);
      else if (type === "equipment") await api.post("/equipment-catalog", { ...f, type: "equipment" });
      else await api.post("/catalog", { ...f, type: "line_item" });
      toast.success(`Saved to catalog`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="catalog-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><meta.Icon className="w-4 h-4 text-[#003E87]" /> {f.catalog_id ? "Edit" : "New"} {meta.label.toLowerCase()}</DialogTitle>
          <DialogDescription>Reusable across every bid. Edits here never change a bid that already priced it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs text-slate-500">Name</span><Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="catalog-edit-name" /></label>
            <label className="block"><span className="text-xs text-slate-500">Category</span>
              <div className="mt-1"><Combobox value={f.category || ""} onChange={(v) => set("category", v)} options={categoryOptions} placeholder="Foundations, Racking, DC…" testid="catalog-edit-category" /></div>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs text-slate-500">Cost code</span>
              <div className="mt-1"><CostCodeCombobox value={f.cost_code || ""} onChange={(v) => set("cost_code", v)} testid="catalog-edit-code" /></div>
            </label>
            {type !== "equipment" && (
              <label className="block"><span className="text-xs text-slate-500">Measure</span>
                <select value={f.measure_type || "count"} onChange={(e) => set("measure_type", e.target.value)} className="w-full mt-1 h-9 text-sm border border-slate-200 rounded px-2 bg-white" data-testid="catalog-edit-measure">
                  <option value="count">Count (ea)</option><option value="linear">Linear (ft)</option><option value="area">Area (sf)</option>
                </select>
              </label>
            )}
          </div>
          {type !== "equipment" && (
            <label className="block"><span className="text-xs text-slate-500">Counts as <span className="text-slate-400">(default proposal quantity tag for lines built from this item)</span></span>
              <div className="mt-1"><KindSelect value={f.kind || ""} onChange={(v) => set("kind", v)} testid="catalog-edit-kind" className="h-9 text-sm" /></div>
            </label>
          )}

          {type === "line_item" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs text-slate-500">Labor / unit</span><div className="mt-1"><LaborHoursInput value={f.unit_labor_hours === 0 || f.unit_labor_hours == null ? "" : f.unit_labor_hours} onChange={(v) => set("unit_labor_hours", v === "" ? "" : num(v))} defaultUnit="min" testid="catalog-edit-labor-hours" className="w-full h-9 text-sm border border-slate-200 rounded px-2 font-mono" /></div></label>
                <label className="block"><span className="text-xs text-slate-500">Material $ / unit {f.commodity_index_id ? "(baseline)" : ""}</span><Input type="number" step="any" value={f.unit_material_cost ?? ""} onChange={(e) => set("unit_material_cost", num(e.target.value))} className="font-mono" data-testid="catalog-edit-material" /></label>
              </div>
              <label className="block">
                <span className="text-xs text-slate-500">Commodity index (auto-adjusts material with the market)</span>
                <select value={f.commodity_index_id || ""} onChange={(e) => set("commodity_index_id", e.target.value || null)} className="w-full mt-1 h-9 text-sm border border-slate-200 rounded px-2 bg-white" data-testid="catalog-edit-index">
                  <option value="">— not linked (fixed price) —</option>
                  {indices.map((i) => <option key={i.index_id} value={i.index_id}>{i.name}{i.current_price != null ? ` — $${Number(i.current_price).toLocaleString("en-US", { maximumFractionDigits: 4 })} ${i.unit || ""}` : ""}</option>)}
                </select>
                {f.commodity_index_id && (
                  <span className="block text-[11px] text-emerald-700 mt-1">
                    Linked. Saving anchors this material price to today's index level — it then auto-adjusts as the market moves. Bids freeze the price on add; only a revision can reprice.
                  </span>
                )}
              </label>
              {!f.commodity_index_id && (
                <label className="block" data-testid="catalog-no-index-reason-wrap">
                  <span className="text-xs text-amber-700">No commodity index — reason required</span>
                  <Input value={f.no_index_reason || ""} onChange={(e) => set("no_index_reason", e.target.value)}
                    placeholder="e.g. labor-only item, or price is fixed by contract" data-testid="catalog-no-index-reason" />
                  <span className="block text-[10px] text-slate-400 mt-0.5">Every catalog item should track an index. If it genuinely can't, note why so estimates stay auditable.</span>
                </label>
              )}
            </>
          )}

          {type === "equipment" && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <label className="block"><span className="text-[10px] text-slate-400">Rental rate $</span><Input type="number" step="any" value={f.rate ?? ""} onChange={(e) => set("rate", num(e.target.value))} className="font-mono" data-testid="catalog-edit-rate" /></label>
              <label className="block"><span className="text-[10px] text-slate-400">Per</span>
                <select value={f.rate_basis || "day"} onChange={(e) => set("rate_basis", e.target.value)} className="w-full mt-1 h-9 text-sm border border-slate-200 rounded px-2 bg-white"><option value="day">day</option><option value="week">week</option><option value="month">month</option></select></label>
              <label className="block"><span className="text-[10px] text-slate-400">Fuel gal/hr</span><Input type="number" step="any" value={f.fuel_gph ?? ""} onChange={(e) => set("fuel_gph", num(e.target.value))} className="font-mono" data-testid="catalog-edit-fuel" /></label>
              <label className="block"><span className="text-[10px] text-slate-400">Delivery $</span><Input type="number" step="any" value={f.delivery_cost ?? ""} onChange={(e) => set("delivery_cost", num(e.target.value))} className="font-mono" data-testid="catalog-edit-delivery" /></label>
              <p className="col-span-full text-[10px] text-slate-400">Fuel uses each project's site diesel price. Sales tax &amp; environmental fees are now global (Catalog → Fees).</p>
            </div>
          )}

          {type === "assembly" && (
            <div>
              <div className="grid grid-cols-2 gap-3 mb-2">
                <label className="block"><span className="text-xs text-slate-500">Per unit (base size)</span><Input type="number" step="any" value={f.per_unit ?? 1} onChange={(e) => set("per_unit", num(e.target.value))} className="font-mono" data-testid="catalog-edit-per-unit" /></label>
                <label className="block"><span className="text-xs text-slate-500">Time basis</span>
                  <select value={f.time_basis || "man"} onChange={(e) => set("time_basis", e.target.value)} className="w-full mt-1 h-9 text-sm border border-slate-200 rounded px-2 bg-white" data-testid="catalog-edit-time-basis"><option value="man">Per-person man time</option><option value="crew">Crew / machine elapsed</option></select></label>
              </div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Components</div>
              <div className="grid grid-cols-12 gap-1 text-[9px] uppercase text-slate-400 px-1"><span className="col-span-4">Name</span><span className="col-span-2">Qty</span><span className="col-span-3">Time ea</span><span className="col-span-2">$ ea</span><span /></div>
              {(f.components || []).map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-1 mb-1" data-testid={`catalog-comp-${i}`}>
                  <Input value={c.name} onChange={(e) => setC(i, "name", e.target.value)} className="col-span-4 h-8 text-xs" placeholder="Item" />
                  <QtyInput value={c.qty} onChange={(v) => setC(i, "qty", v)} className="col-span-2 h-8 text-xs font-mono rounded-md border border-slate-200 px-2 bg-transparent" testid={`catalog-comp-qty-${i}`} />
                  <div className="col-span-3"><LaborHoursInput value={c.man_hours === 0 || c.man_hours == null ? "" : c.man_hours} onChange={(v) => setC(i, "man_hours", v === "" ? 0 : num(v))} defaultUnit="min" testid={`catalog-comp-mh-${i}`} className="w-full h-8 text-xs border border-slate-200 rounded px-1 font-mono" /></div>
                  <Input type="number" step="any" value={c.material_cost} onChange={(e) => setC(i, "material_cost", num(e.target.value))} className="col-span-2 h-8 text-xs font-mono" />
                  <button onClick={() => delC(i)} className="text-slate-300 hover:text-rose-600 flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <button onClick={addC} className="text-[11px] text-[#003E87] hover:underline flex items-center gap-1 mt-1" data-testid="catalog-comp-add"><Plus className="w-3 h-3" /> Add component</button>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} className="bg-[#003E87] hover:bg-[#00306a]" data-testid="catalog-edit-save">Save to catalog</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CatalogImportExport({ open, onOpenChange, onImported }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const reset = () => { setResult(null); setProgress(null); if (fileRef.current) fileRef.current.value = ""; };
  useEffect(() => { if (!open) reset(); }, [open]);

  const blob = (data, filename) => {
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  const download = async () => {
    try {
      const r = await api.get("/catalog/workbook", { responseType: "blob" });
      blob(r.data, "gridforge_catalog_workbook.xlsx");
      toast.success("Catalog workbook downloaded — fill in any sheet and upload it back");
    } catch { toast.error("Could not download the workbook"); }
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true); setResult(null); setProgress(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/import/catalog-workbook", fd);
      if (r.data.processing && r.data.job_id) {
        // Long imports run as a background job — poll it so we can show a live progress bar
        // instead of appearing to hang. Big libraries can take a minute or two.
        setProgress({ done: 0, total: r.data.total || 0, startedAt: Date.now() });
        for (let i = 0; i < 1800; i++) {
          await new Promise((res) => setTimeout(res, 1000));
          let st;
          try { st = (await api.get(`/catalog/imports/jobs/${r.data.job_id}`)).data; }
          catch { continue; }
          setProgress((p) => ({ done: st.done || 0, total: st.total || 0, startedAt: p?.startedAt || Date.now() }));
          if (st.status === "done") {
            const done = { committed: true, imported: st.imported || {}, unindexed: st.unindexed || [], batch_id: st.batch_id };
            setResult(done);
            const n = Object.values(done.imported).reduce((a, b) => a + b, 0);
            toast.success(`Imported ${n} item${n === 1 ? "" : "s"} into the catalog`);
            onImported?.();
            break;
          }
          if (st.status === "failed") { toast.error(st.error || "Could not finish the import"); break; }
        }
      } else {
        setResult(r.data);
        toast.error("Some rows need fixing — see the details below");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Could not read that file"); }
    finally { setBusy(false); setProgress(null); if (fileRef.current) fileRef.current.value = ""; }
  };
  const SHEET_LABEL = { catalog_materials: "Line items", catalog_equipment: "Equipment", catalog_assemblies: "Assemblies" };
  const undoImport = async () => {
    if (!result?.batch_id) return;
    setBusy(true);
    try {
      const r = await api.post(`/catalog/imports/${result.batch_id}/undo`);
      toast.success(`Import undone — ${r.data.restored} restored, ${r.data.deleted} removed`);
      setResult(null); onImported?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not undo the import"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="catalog-io-dialog">
        <DialogHeader>
          <DialogTitle>Catalog workbook — import &amp; export</DialogTitle>
          <DialogDescription>One Excel workbook for your entire catalog — line items, equipment and assemblies on their own tabs, with dropdowns for cost codes, categories and commodity indexes. Download it, fill it in (or hand it to an AI), and upload it back. Items matching an existing name update in place; new names are added. Nothing is deleted.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={download} data-testid="catalog-io-download">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download catalog workbook
            </Button>
            <label className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#003E87] text-white text-xs hover:bg-[#00306a] cursor-pointer" data-testid="catalog-io-upload-label">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Import filled workbook
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onFile} disabled={busy} data-testid="catalog-io-upload-input" />
            </label>
          </div>
          {busy && progress && (
            <div data-testid="catalog-io-progress">
              <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                <span className="flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Importing your catalog… you can keep this open.</span>
                <span className="flex items-center gap-2">
                  {etaText(progress) && <span className="text-slate-400" data-testid="catalog-io-progress-eta">{etaText(progress)}</span>}
                  <span className="font-mono" data-testid="catalog-io-progress-label">{progress.done} of {progress.total || "?"} items</span>
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-[#003E87] transition-all duration-300" style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 8}%` }} data-testid="catalog-io-progress-fill" />
              </div>
            </div>
          )}
          {result && !result.committed && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2" data-testid="catalog-io-errors-panel">
              <div className="flex items-center gap-1.5 text-sm font-medium text-amber-800">
                <AlertTriangle className="w-4 h-4" /> Nothing was imported — fix these and upload again
              </div>
              {Object.entries(result.sheets || {}).map(([ent, s]) => (
                <div key={ent} className="text-xs" data-testid={`catalog-io-sheet-${ent}`}>
                  <div className="font-semibold text-slate-700">{SHEET_LABEL[ent] || ent} — {s.count} valid{s.error_count ? `, ${s.error_count} error${s.error_count === 1 ? "" : "s"}` : ""}</div>
                  {s.fatal && <div className="text-rose-700 mt-0.5">{s.fatal}</div>}
                  {s.errors?.length > 0 && (
                    <ul className="mt-1 max-h-28 overflow-auto text-[11px] text-rose-600 space-y-0.5 list-disc pl-4">
                      {s.errors.slice(0, 20).map((er, i) => <li key={i}>Row {er.row}, {er.column}: {er.message}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
          {result && result.committed && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-2" data-testid="catalog-io-result">
              <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Imported {Object.values(result.imported || {}).reduce((a, b) => a + b, 0)} item{Object.values(result.imported || {}).reduce((a, b) => a + b, 0) === 1 ? "" : "s"} into the catalog
              </div>
              <div className="text-xs text-emerald-700 flex flex-wrap gap-x-4 gap-y-0.5">
                {Object.entries(result.imported || {}).map(([ent, n]) => (
                  <span key={ent} data-testid={`catalog-io-imported-${ent}`}>{SHEET_LABEL[ent] || ent}: <b>{n}</b></span>
                ))}
              </div>
              {result.unindexed?.length > 0 && (
                <div className="text-xs text-amber-700" data-testid="catalog-io-unindexed">
                  <div className="font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {result.unindexed.length} item{result.unindexed.length === 1 ? "" : "s"} need index review</div>
                  <div className="text-amber-600 mt-0.5">These name a commodity index that isn't set up yet, so they track nothing until fixed: {result.unindexed.slice(0, 8).map((u) => `${u.name} (“${u.value}”)`).join(", ")}{result.unindexed.length > 8 ? "…" : ""}</div>
                </div>
              )}
              <div className="flex justify-end gap-2">
                {result.batch_id && (
                  <Button size="sm" variant="outline" onClick={undoImport} disabled={busy} data-testid="catalog-io-undo" className="border-rose-200 text-rose-700 hover:bg-rose-50">
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Undo this import
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => onOpenChange(false)} data-testid="catalog-io-done">Done</Button>
              </div>
            </div>
          )}
          <p className="text-[11px] text-slate-400">Assemblies encode each component as <code className="text-slate-500">name|qty|hrs|$|cost_code|index</code>, separated by <code className="text-slate-500">;</code>. See the workbook's <b>Instructions</b> and <b>Reference</b> tabs for the exact format and allowed values.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
